import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Shield,
  CheckCircle,
  Clock,
  AlertCircle,
  Search,
  ArrowRight,
  DollarSign,
  FileCheck,
  Lock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EscrowPage() {
  const { user } = useAuth();
  const [escrowRecords, setEscrowRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchEscrowRecords();
  }, []);

  const fetchEscrowRecords = async () => {
    try {
      const response = await axios.get(`${API}/escrow`);
      setEscrowRecords(response.data);
    } catch (error) {
      console.error("Failed to fetch escrow records:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleVerify = async (escrowId) => {
    try {
      await axios.post(`${API}/escrow/${escrowId}/verify`);
      toast.success("Escrow milestone verified successfully!");
      fetchEscrowRecords();
    } catch (error) {
      toast.error("Failed to verify escrow");
    }
  };

  const handleRelease = async (escrowId) => {
    try {
      await axios.post(`${API}/escrow/${escrowId}/release`);
      toast.success("Escrow funds released successfully!");
      fetchEscrowRecords();
    } catch (error) {
      toast.error("Failed to release escrow");
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case "completed":
        return { icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" };
      case "in_progress":
        return { icon: Clock, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" };
      case "pending":
        return { icon: AlertCircle, color: "text-slate-400", bg: "bg-slate-50", border: "border-slate-200" };
      default:
        return { icon: AlertCircle, color: "text-slate-400", bg: "bg-slate-50", border: "border-slate-200" };
    }
  };

  const filteredRecords = escrowRecords.filter((record) => {
    const matchesSearch = record.project_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          record.milestone_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === "all" || record.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Group by project
  const projectGroups = filteredRecords.reduce((acc, record) => {
    if (!acc[record.project_id]) {
      acc[record.project_id] = {
        project_name: record.project_name,
        industry: record.industry,
        milestones: []
      };
    }
    acc[record.project_id].milestones.push(record);
    return acc;
  }, {});

  const totalEscrow = escrowRecords.reduce((sum, r) => sum + r.amount, 0);
  const pendingRelease = escrowRecords.filter(r => r.status === "completed").reduce((sum, r) => sum + r.amount, 0);
  const inProgress = escrowRecords.filter(r => r.status === "in_progress").reduce((sum, r) => sum + r.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="escrow-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Escrow Management</h1>
        <p className="text-slate-500 mt-1">Monitor and manage milestone-based fund releases</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="border-slate-200" data-testid="stat-total-escrow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Escrow</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(totalEscrow)}</p>
              </div>
              <div className="w-10 h-10 bg-[#E8F4FD] rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-[#3B82F6]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="stat-pending-release">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Release</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">{formatCurrency(pendingRelease)}</p>
              </div>
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="stat-in-progress">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">In Progress</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{formatCurrency(inProgress)}</p>
              </div>
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="stat-milestones">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Milestones</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{escrowRecords.length}</p>
              </div>
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Escrow Flow Diagram */}
      <Card className="border-slate-200" data-testid="escrow-flow">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">Escrow Process Flow</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between max-w-3xl mx-auto py-4">
            {[
              { label: "Funds Received", icon: DollarSign, status: "completed" },
              { label: "Verification", icon: FileCheck, status: "in_progress" },
              { label: "Token Release", icon: CheckCircle, status: "pending" }
            ].map((step, idx) => (
              <div key={idx} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                    step.status === "completed" ? "bg-emerald-500" :
                    step.status === "in_progress" ? "bg-amber-500" : "bg-slate-200"
                  }`}>
                    <step.icon className={`w-7 h-7 ${
                      step.status === "pending" ? "text-slate-500" : "text-white"
                    }`} />
                  </div>
                  <p className={`mt-2 text-sm font-medium ${
                    step.status === "completed" ? "text-emerald-600" :
                    step.status === "in_progress" ? "text-amber-600" : "text-slate-500"
                  }`}>
                    {step.label}
                  </p>
                </div>
                {idx < 2 && (
                  <ArrowRight className="w-8 h-8 mx-4 text-slate-300" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search projects or milestones..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="escrow-search"
          />
        </div>
        <div className="flex gap-2">
          {["all", "completed", "in_progress", "pending"].map((status) => (
            <Button
              key={status}
              variant={filterStatus === status ? "default" : "outline"}
              onClick={() => setFilterStatus(status)}
              className={filterStatus === status ? "bg-[#0F2744]" : ""}
              data-testid={`filter-${status}`}
            >
              {status === "all" ? "All" : status.replace("_", " ")}
            </Button>
          ))}
        </div>
      </div>

      {/* Escrow Records by Project */}
      <div className="space-y-6">
        {Object.entries(projectGroups).map(([projectId, project]) => (
          <Card key={projectId} className="border-slate-200" data-testid={`escrow-project-${projectId}`}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold text-slate-900">{project.project_name}</CardTitle>
                  <span className="industry-tag mt-1">{project.industry}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Total Escrow</p>
                  <p className="font-semibold text-slate-900">
                    {formatCurrency(project.milestones.reduce((sum, m) => sum + m.amount, 0))}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {project.milestones.map((milestone) => {
                  const statusInfo = getStatusInfo(milestone.status);
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <div
                      key={milestone.id}
                      className={`p-4 rounded-lg border ${statusInfo.bg} ${statusInfo.border}`}
                      data-testid={`escrow-milestone-${milestone.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <StatusIcon className={`w-5 h-5 ${statusInfo.color}`} />
                          <div>
                            <p className="font-medium text-slate-900">{milestone.milestone_name}</p>
                            <p className="text-sm text-slate-500">Target: {formatCurrency(milestone.amount)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            milestone.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                            milestone.status === "in_progress" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>
                            {milestone.status.replace("_", " ")}
                          </span>
                          
                          {user?.role === "admin" && milestone.status === "in_progress" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleVerify(milestone.id)}
                              data-testid={`verify-${milestone.id}`}
                            >
                              Verify
                            </Button>
                          )}
                          
                          {user?.role === "admin" && milestone.status === "completed" && (
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => handleRelease(milestone.id)}
                              data-testid={`release-${milestone.id}`}
                            >
                              Release Funds
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {Object.keys(projectGroups).length === 0 && (
        <div className="text-center py-12">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No escrow records found.</p>
        </div>
      )}
    </div>
  );
}
